import { useEffect, useState } from "react"
import {Star, ExternalLink, Quote} from 'lucide-react'

const GOOGLE_REVIEWS_FUNCTION_URL = "https://api.lumi.new/v1/functions/p485856725183959040/getGoogleReviews"

interface GoogleReview {
  authorName: string
  authorUrl: string
  profilePhotoUrl: string
  rating: number
  text: string
  relativeTime: string
  translated: boolean
}

interface ReviewsPayload {
  source: "google"
  placeName: string
  rating: number | null
  userRatingCount: number | null
  mapsUrl: string
  reviews: GoogleReview[]
}

function StarRow({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, idx) => (
        <Star
          key={idx}
          size={13}
          className={idx < Math.round(rating) ? "fill-[#b85c38] text-[#b85c38]" : "fill-transparent text-[#d6c4b6]"}
        />
      ))}
    </div>
  )
}

interface GoogleReviewsProps {
  title?: string
  description?: string
  keywords?: string[]
  limit?: number
  enableJsonLd?: boolean
}

function syncLocalBusinessJsonLd(payload: ReviewsPayload) {
  if (typeof document === "undefined") return
  if (payload.rating == null || payload.userRatingCount == null || payload.userRatingCount < 1) return

  const script = document.getElementById("local-business-jsonld")
  if (!script) return

  try {
    const json = JSON.parse(script.textContent || "{}") as Record<string, unknown>
    json.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: Number(payload.rating.toFixed(1)),
      bestRating: 5,
      worstRating: 1,
      ratingCount: payload.userRatingCount,
    }
    json.review = payload.reviews.slice(0, 5).map((review) => ({
      "@type": "Review",
      author: {
        "@type": "Person",
        name: review.authorName,
      },
      reviewBody: review.text,
      reviewRating: {
        "@type": "Rating",
        ratingValue: review.rating,
        bestRating: 5,
        worstRating: 1,
      },
      publisher: {
        "@type": "Organization",
        name: "Google",
      },
    }))
    if (payload.mapsUrl) {
      json.hasMap = payload.mapsUrl
    }
    script.textContent = JSON.stringify(json)
  } catch {
    // Le JSON-LD statique reste en place si la mise à jour échoue.
  }
}

function GoogleReviews({
  title = "Ce que disent les visiteuses de la fiche Google",
  description,
  keywords,
  limit = 6,
  enableJsonLd = false,
}: GoogleReviewsProps) {
  const [data, setData] = useState<ReviewsPayload | null>(null)
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading")

  useEffect(() => {
    fetch(GOOGLE_REVIEWS_FUNCTION_URL)
      .then((res) => {
        if (!res.ok) throw new Error("request_failed")
        return res.json()
      })
      .then((payload: ReviewsPayload) => {
        if (!payload?.reviews || payload.reviews.length === 0) {
          setStatus("error")
          return
        }
        setData(payload)
        setStatus("success")
        if (enableJsonLd) {
          syncLocalBusinessJsonLd(payload)
        }
      })
      .catch(() => setStatus("error"))
  }, [enableJsonLd])

  if (status === "loading") {
    return (
      <section className="border-y border-[#e2d5c8] bg-[#fbf7f2] py-20">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="flex items-center justify-center gap-3 text-sm text-[#8c6d58]">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#d6c4b6] border-t-[#b85c38]" />
            <span>Chargement des avis Google…</span>
          </div>
        </div>
      </section>
    )
  }

  if (status === "error" || !data) {
    return null
  }

  const matchedReviews =
    keywords && keywords.length > 0
      ? data.reviews.filter((review) =>
          keywords.some((keyword) => review.text.toLowerCase().includes(keyword.toLowerCase()))
        )
      : data.reviews
  const displayedReviews = matchedReviews.length > 0 ? matchedReviews : data.reviews

  return (
    <section className="border-y border-[#e2d5c8] bg-[#fbf7f2] py-20">
      <div className="mx-auto max-w-6xl px-5 md:px-8">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.28em] text-[#b85c38]">
              <GoogleBadge />
              <span>Avis Google</span>
            </div>
            <h2 className="mt-3 font-display text-4xl text-[#2d221b] md:text-5xl">
              {title}
            </h2>
            {description ? (
              <p className="mt-2 max-w-xl text-sm text-[#685548]">{description}</p>
            ) : null}
            {data.rating !== null ? (
              <div className="mt-4 flex items-center gap-3">
                <StarRow rating={data.rating} />
                <span className="font-display text-xl font-semibold text-[#2d221b]">{data.rating.toFixed(1)}</span>
                {data.userRatingCount !== null ? (
                  <span className="text-sm text-[#8c6d58]">
                    sur {data.userRatingCount} avis
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>

          <a
            href={data.mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex w-fit items-center gap-2 rounded-full border border-[#d6c4b6] bg-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#6f4b3e] shadow-sm transition hover:border-[#b85c38] hover:text-[#b85c38]"
          >
            <span>Voir tous les avis sur Google</span>
            <ExternalLink size={14} />
          </a>
        </div>

        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {displayedReviews.slice(0, limit).map((review, idx) => (
            <article
              key={`${review.authorName}-${idx}`}
              className="relative flex flex-col rounded-3xl border border-[#e2d5c8] bg-white p-6 shadow-warm-xs transition-all duration-300 hover:-translate-y-1 hover:shadow-warm-md"
            >
              <Quote size={22} className="text-[#e8c9b8]" />

              <p className="mt-4 flex-1 text-sm leading-relaxed text-[#5e4b3e] line-clamp-5">
                {review.text}
              </p>

              <div className="mt-5 flex items-center gap-3 border-t border-[#eee2d6] pt-4">
                {review.profilePhotoUrl ? (
                  <img
                    src={review.profilePhotoUrl}
                    alt={review.authorName}
                    loading="lazy"
                    className="h-10 w-10 shrink-0 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#b85c38] to-[#9d4c2e] font-display text-sm text-white">
                    {review.authorName.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-[#2d221b]">{review.authorName}</p>
                  <div className="mt-0.5 flex items-center gap-2">
                    <StarRow rating={review.rating} />
                    {review.relativeTime ? (
                      <span className="text-[11px] text-[#a08a76]">{review.relativeTime}</span>
                    ) : null}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        <p className="mt-8 text-center text-[11px] text-[#a08a76]">
          Avis publiés tels quels, collectés via Google. {data.placeName}
        </p>
      </div>
    </section>
  )
}

function GoogleBadge() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M23.52 12.27c0-.82-.07-1.6-.21-2.36H12v4.47h6.47c-.28 1.5-1.13 2.77-2.4 3.62v3.01h3.87c2.27-2.09 3.58-5.17 3.58-8.74z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.96-1.07 7.94-2.9l-3.87-3.01c-1.08.72-2.46 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.27v3.11C3.24 21.3 7.26 24 12 24z"
      />
      <path
        fill="#FBBC05"
        d="M5.27 14.28A7.17 7.17 0 0 1 4.9 12c0-.79.13-1.56.37-2.28V6.61H1.27A11.98 11.98 0 0 0 0 12c0 1.93.46 3.76 1.27 5.39l4-3.11z"
      />
      <path
        fill="#EA4335"
        d="M12 4.75c1.76 0 3.34.6 4.58 1.78l3.43-3.43C17.95 1.19 15.24 0 12 0 7.26 0 3.24 2.7 1.27 6.61l4 3.11C6.22 6.86 8.87 4.75 12 4.75z"
      />
    </svg>
  )
}

export default GoogleReviews
